import DialogComp from 'components/Dialog';
import ToggleGroupComponent from 'components/ToggleGroup';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { EditIcon, PlusIcon, SaveIcon, TrashIcon } from 'lucide-react';
import React from 'react';
import { deleteAltID, getAltIDs, patchAltID, postAltID } from 'utils/fetchers';
import { omit } from 'utils/index';
import AccessRulesList from '../../App/OrgView/AccessRulesList';

const AIDForm = ({ aid, editing = false, onEditField }: IAIDForm) => {
  return (
    <>
      <td>
        {editing ? (
          <Input
            type="text"
            id="type"
            className="w-auto"
            value={aid.type}
            onChange={e => onEditField('type', e.target.value)}
            placeholder="Type"
          />
        ) : (
          aid.type
        )}
      </td>
      <td>
        {editing ? (
          <Input
            type="text"
            id="content"
            className="w-auto"
            value={aid.content}
            onChange={e => onEditField('content', e.target.value)}
            placeholder="Content"
          />
        ) : (
          aid.content
        )}
      </td>
    </>
  );
};

interface IAIDForm {
  aid: Partial<IAltID>;
  editing: boolean;
  onEditField: (field: keyof IAltID, data: string) => void;
}

const UserForm = ({ data, newData, onChangeData, accessGroups }: IUserForm) => {
  const combinedData = { ...data, ...newData };
  const [altIDs, setAltIDs] = React.useState<Array<IAltID>>([]);
  const [editingAID, setEditingAID] = React.useState<Partial<IAltID & { new?: true }> | null>(null);

  React.useEffect(() => {
    if (data.userId && data.organization) {
      getAltIDs(data.userId, data.organization).then(ids => {
        if (Array.isArray(ids)) setAltIDs(ids);
      });
    }
  }, []);

  const onSaveClickAID = () => {
    // Should have all the relevant info
    if (data.userId && editingAID?.type && editingAID.content) {
      if (editingAID.new) {
        postAltID(data.userId, omit(['new'], editingAID as IAltID), data.organization).then(suc => {
          if (suc) {
            setAltIDs([suc, ...altIDs]);
            setEditingAID(null);
          }
        });
      } else if (editingAID.id) {
        patchAltID(data.userId, editingAID.id, editingAID as IAltID, data.organization).then(suc => {
          if (suc) {
            setAltIDs(altIDs.map(aid => (aid.id === editingAID.id ? (editingAID as IAltID) : aid)));
            setEditingAID(null);
          }
        });
      }
    }
  };

  const onEditIdField = (field: keyof IAltID, data: string) => {
    if (editingAID) setEditingAID({ ...editingAID, [field]: data });
  };

  return (
    <div className="grid gap-y-4">
      <div>
        <Label htmlFor="userId">User id (filled automatically by server)</Label>
        <Input
          type="text"
          id="userId"
          className="w-100"
          value={combinedData.userId}
          onChange={e => onChangeData('userId', e.target.value)}
          placeholder="User id"
          disabled
        />
      </div>
      <div>
        <Label htmlFor="lastNames">Last names</Label>
        <Input
          type="text"
          id="lastNames"
          className="w-100"
          value={combinedData.lastNames}
          onChange={e => onChangeData('lastNames', e.target.value)}
          placeholder="Last names"
        />
      </div>
      <div>
        <Label htmlFor="firstNames">First names</Label>
        <Input
          type="text"
          id="firstNames"
          className="w-100"
          value={combinedData.firstNames}
          onChange={e => onChangeData('firstNames', e.target.value)}
          placeholder="First names"
        />
      </div>
      <div>
        <Label htmlFor="organization">Organization (filled automatically by server)</Label>
        <Input
          type="text"
          id="organization"
          className="w-100"
          value={combinedData.organization}
          onChange={e => onChangeData('organization', e.target.value)}
          placeholder="Organization"
          disabled={true}
        />
      </div>
      <div>
        <ToggleGroupComponent
          value={(newData || data)['accessGroups'] || []}
          onChange={acs => onChangeData('accessGroups', acs)}
          options={accessGroups.map(ac => ac.groupId)}
          header={'Access groups'}
          optionFormatter={opt => accessGroups.find(ac => ac.groupId === opt)?.groupName || opt}
        />
      </div>
      {/* <div>
        <Label htmlFor="capabilities">Capabilities (comma separated list)</Label>
        <Input
          type="text"
          id="capabilities"
          className="w-100"
          value={combinedData.capabilities}
          onChange={e => onChangeData('capabilities', e.target.value)}
          placeholder="Capabilities"
        />
      </div> */}
      <div>
        <Label htmlFor="uILanguage">UI Language (leave empty for fi)</Label>
        <Input
          type="text"
          id="uILanguage"
          className="w-100"
          value={combinedData.uILanguage}
          onChange={e => onChangeData('uILanguage', e.target.value)}
          placeholder="UI Language"
        />
      </div>
      <div>
        <Label htmlFor="active">Active</Label>
        <Input
          type="Checkbox"
          id="active"
          name="active"
          className="w-100"
          checked={combinedData.active === true || (combinedData.active as unknown as string) === 'active'}
          value={'active'}
          onChange={() => {
            const checked = combinedData.active === true || (combinedData.active as unknown as string) === 'active';
            onChangeData('active', !checked);
          }}
          placeholder="Active"
        />
      </div>
      {data.userId && (
        <div className="mb-4">
          <div className="flex w-full mb-2">
            <div className="w-50">
              <Label>Alt IDs</Label>
            </div>
            <div className="flex w-full justify-end">
              <Button variant="outline" className="h-9" onClick={() => setEditingAID({ new: true })}>
                <PlusIcon strokeWidth={2} />
                <div>New Alt ID</div>
              </Button>
            </div>
          </div>
          <div className="tableArea rounded-md">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Content</th>
                  <th style={{ width: 'auto' }}></th>
                </tr>
              </thead>
              <tbody>
                {[...(editingAID?.new ? [editingAID] : []), ...altIDs].map(aid => (
                  <tr key={'new' in aid && aid?.new ? 'new' : aid.id}>
                    <AIDForm
                      aid={editingAID && editingAID?.id === aid.id ? editingAID : aid}
                      editing={editingAID?.id === aid.id}
                      onEditField={onEditIdField}
                    />
                    <td>
                      {editingAID && editingAID.id === aid.id ? (
                        <>
                          <Button
                            onClick={onSaveClickAID}
                            disabled={!editingAID.type || !editingAID.content}
                            className="h-9"
                          >
                            <SaveIcon /> Save
                          </Button>
                        </>
                      ) : editingAID ? (
                        <></>
                      ) : (
                        <div className="flex w-full justify-end">
                          <DialogComp
                            openButton={
                              <Button size={'icon'}>
                                <TrashIcon />
                              </Button>
                            }
                            title="Do you really want to delete this Alt ID?"
                            cancel={{ callback: () => '' }}
                            accept={{
                              callback: () => {
                                if (aid.id && data.userId && data.organization)
                                  deleteAltID(aid.id, data.userId, data.organization).then(suc => {
                                    if (suc) {
                                      setAltIDs(altIDs.filter(a => a.id !== aid.id));
                                    }
                                  });
                              },
                            }}
                          />

                          <Button size={'icon'} className="ml-2" onClick={() => setEditingAID(aid)}>
                            <EditIcon />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.userId && data.organization ? (
        <AccessRulesList data={{ organization: data.organization, userId: data.userId }} />
      ) : (
        <></>
      )}
    </div>
  );
};

interface IUserForm {
  data: Partial<TVaultUser & { active?: boolean | string }>;
  newData: Partial<TVaultUser & { active?: boolean | string }>;
  onChangeData: (field: keyof TVaultUser, value: string | boolean | string[]) => void;
  accessGroups: TVaultAccessGroup[];
}

export default UserForm;
